import {Alert, Box, TextField, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from 'react';
import {useProfile} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ExternalInfo} from "crqlar-react-common/dist/models/common/external.model";
import {getCurrentStay} from "crqlar-react-common/dist/models/profile/profile.model";
import {FOLIO_ALERT_SX, FOLIO_FIELD_GAP} from "../editor/folioStyles";
import {getPrimaryEmail} from "crqlar-react-common/dist/utils/profile.util";

interface ApaleoProviderProps {
    onDataChange: (isValid: boolean, data: any) => void;
    profileId?: string;
    /** The folio dialog's payment block, beside its totals: no padding of its own, small toggle, fields and alerts. */
    compact?: boolean;
}

const ApaleoProvider = ({profileId, onDataChange, compact}: ApaleoProviderProps) => {
    const {t} = useTranslation();
    const fieldSize = compact ? 'small' : undefined;
    const alertSx = compact ? FOLIO_ALERT_SX : undefined;

    const profile = useProfile(profileId);
    const [mode, setMode] = useState<'internal' | 'external'>('internal');
    const [reservation, setReservation] = useState<Reservation | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [apaleoExternalInfo, setApaleoExternalInfo] = useState<ExternalInfo | null>(null);

    // External mode form data
    const [externalFormData, setExternalFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
    });
    const [formErrors, setFormErrors] = useState({
        firstName: '',
        lastName: ''
    });

    useEffect(() => {
        if (mode === 'internal') {
            const fetchReservationData = async () => {
                if (!getCurrentStay(profile)?.reservationId) {
                    setError(t('No current stay or reservation found'));
                    onDataChange(false, null);
                    return;
                }

                setLoading(true);
                setError(null);

                try {
                    const reservationData = await ReservationService.getReservationById(getCurrentStay(profile)?.reservationId as string);
                    setReservation(reservationData);

                    // Find Apaleo external info
                    const apaleoInfo = reservationData.externalInfo?.find(
                        (info: ExternalInfo) => info.provider === 'apaleo'
                    );

                    if (apaleoInfo) {
                        setApaleoExternalInfo(apaleoInfo);
                        onDataChange(true, {
                            provider: 'apaleo',
                            externalReservationId: apaleoInfo.id,
                            firstName: profile?.firstName,
                            lastName: profile?.lastName,
                            email: getPrimaryEmail(profile),
                            phone: profile?.phones?.[0]?.phoneNumberFull,
                        });
                    } else {
                        setError(t('No Apaleo reservation found for this guest'));
                        onDataChange(false, null);
                    }
                } catch (err) {
                    console.error('Error fetching reservation:', err);
                    setError(t('Failed to fetch reservation data'));
                    onDataChange(false, null);
                } finally {
                    setLoading(false);
                }
            };

            if (profile?.id) {
                fetchReservationData();
            }
        } else {
            // External mode - reset internal state
            setReservation(null);
            setError(null);
            setApaleoExternalInfo(null);
            setLoading(false);
        }
    }, [mode, profile?.id, profile?.currentStay?.reservationId]);

    // Validate external form and update parent
    useEffect(() => {
        if (mode === 'external') {
            const isFirstNameValid = externalFormData.firstName.trim() !== '';
            const isLastNameValid = externalFormData.lastName.trim() !== '';
            const isValid = isFirstNameValid && isLastNameValid;

            setFormErrors({
                firstName: isFirstNameValid ? '' : t('First name is required'),
                lastName: isLastNameValid ? '' : t('Last name is required')
            });

            if (isValid) {
                onDataChange(true, {
                    provider: 'apaleo',
                    mode: 'external',
                    firstName: externalFormData.firstName.trim(),
                    lastName: externalFormData.lastName.trim(),
                    email: externalFormData.email.trim(),
                    phone: externalFormData.phone.trim(),
                });
            } else {
                onDataChange(false, null);
            }
        }
    }, [mode, externalFormData]);

    const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'internal' | 'external' | null) => {
        if (newMode !== null) {
            setMode(newMode);
        }
    };

    const handleExternalFormChange = (field: string, value: string) => {
        setExternalFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Box sx={{padding: compact ? 0 : 2}}>
            {/* Toggle Button Group */}
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                aria-label="apaleo mode"
                size={fieldSize}
                fullWidth={compact}
                sx={{mb: compact ? FOLIO_FIELD_GAP : 3}}
            >
                <ToggleButton value="internal" aria-label="internal">
                    {t('Internal')}
                </ToggleButton>
                <ToggleButton value="external" aria-label="external">
                    {t('External')}
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Internal Mode */}
            {mode === 'internal' && (
                <>
                    {loading && (
                        <Typography>
                            {t('Loading Apaleo reservation data...')}
                        </Typography>
                    )}

                    {error && (
                        <>
                            <Alert severity="error" sx={{mb: compact ? 1 : 2, ...alertSx}}>
                                {error}
                            </Alert>
                            <Typography variant="body2">
                                {t('Unable to proceed with Apaleo payment. Please ensure the guest has a valid Apaleo reservation.')}
                            </Typography>
                        </>
                    )}

                    {!loading && !error && !apaleoExternalInfo && (
                        <Alert severity="warning" sx={alertSx}>
                            {t('No Apaleo reservation found for this guest')}
                        </Alert>
                    )}

                    {!loading && !error && apaleoExternalInfo && (
                        <>
                            <Alert severity="success" sx={{mb: compact ? 1 : 2, ...alertSx}}>
                                {t('Apaleo reservation found and validated')}
                            </Alert>

                            <Typography variant="body2" sx={{mb: 1}}>
                                <strong>{t('Crlar Reservation ID')}:</strong> {reservation?.id}
                            </Typography>

                            <Typography variant="body2" sx={{mb: 1}}>
                                <strong>{t('Apaleo External ID')}:</strong> {apaleoExternalInfo.id}
                            </Typography>

                            {reservation?.expectedStartDate && reservation?.expectedEndDate && (
                                <Typography variant="body2" sx={{mb: 1}}>
                                    <strong>{t('Stay Period')}:</strong> {new Date(reservation.expectedStartDate).toLocaleDateString()} - {new Date(reservation.expectedEndDate).toLocaleDateString()}
                                </Typography>
                            )}

                            {reservation?.resourceAdditionalInfo?.[0]?.roomID && (
                                <Typography variant="body2" sx={{mb: 1}}>
                                    <strong>{t('Room')}:</strong> {reservation.resourceAdditionalInfo[0].roomID}
                                </Typography>
                            )}

                            {apaleoExternalInfo.propertyId && (
                                <Typography variant="body2">
                                    <strong>{t('Property ID')}:</strong> {apaleoExternalInfo.propertyId}
                                </Typography>
                            )}
                        </>
                    )}
                </>
            )}

            {/* External Mode */}
            {mode === 'external' && (
                <Box component="form" sx={{display: 'flex', flexDirection: 'column', gap: compact ? FOLIO_FIELD_GAP : 2}}>
                    <TextField
                        size={fieldSize}
                        label={t('First Name')}
                        value={externalFormData.firstName}
                        onChange={(e) => handleExternalFormChange('firstName', e.target.value)}
                        required
                        error={!!formErrors.firstName}
                        helperText={formErrors.firstName}
                        fullWidth
                    />

                    <TextField
                        size={fieldSize}
                        label={t('Last Name')}
                        value={externalFormData.lastName}
                        onChange={(e) => handleExternalFormChange('lastName', e.target.value)}
                        required
                        error={!!formErrors.lastName}
                        helperText={formErrors.lastName}
                        fullWidth
                    />

                    <TextField
                        size={fieldSize}
                        label={t('Email')}
                        type="email"
                        value={externalFormData.email}
                        onChange={(e) => handleExternalFormChange('email', e.target.value)}
                        fullWidth
                    />

                    <TextField
                        size={fieldSize}
                        label={t('Phone')}
                        type="tel"
                        value={externalFormData.phone}
                        onChange={(e) => handleExternalFormChange('phone', e.target.value)}
                        fullWidth
                    />
                </Box>
            )}
        </Box>
    );
};

export default ApaleoProvider;
